'use client';

import type { PropsWithChildren } from 'react';
import type { Collection } from '@/app/actions/collection';
import type { User } from '@/types/user/type';
import type { AnswerType } from '~/model/AnswerType';
import type { ContributionType } from '~/model/ContributionType';
import type { CriteriaChoiceUserType } from '~/model/CriteriaChoiceType';
import type { CriteriaType } from '~/model/CriteriaType';
import type { InteractionType, UserInteractionStatusType } from '~/model/InteractionType';
import type { SportType } from '~/model/SportType';
import type { SelectedTimeSlotType, TimeSlotType } from '~/model/TimeSlotType';
import { useCallback, useEffect, useState } from 'react';
import { fetchPendingCriteria, fetchTimeslots, getContribution } from '@/app/actions/server';
import { MainLoader } from '@/app/components/Loader';
import { canInitialize } from '@/app/utils/contributions/initialize';
import { useRequestState } from '~/hooks/request/useRequestState';
import { CriteriaWrapper } from './CriteriaWrapper';
import { ContributorProvider } from './provider/ContributorProvider';
import { ProfileCompletionProvider } from './provider/ProfileCompletionProvider';
import { TimeslotsProvider } from './provider/TimeslotsProvider';

export const ContributionWrapper = ({
  children,
  contributor,
  interaction,
}: PropsWithChildren<{
  contributor: User;
  interaction: InteractionType;
}>) => {
  const [userStatus, setUserStatus] = useState<UserInteractionStatusType>(null);
  const [answers, setAnswers] = useState<Collection<AnswerType>>(null);
  const [contributions, setContributions] = useState<Collection<ContributionType>>(null);
  const [mandatoryCriteria, setMandatoryCriteria] = useState<Collection<CriteriaType>>(null);
  const [mandatoryCriteriaChoiceUser, setMandatoryCriteriaChoiceUser] = useState<Collection<CriteriaChoiceUserType>>(null);
  const [requestedCriteria, setRequestedCriteria] = useState<Collection<CriteriaType>>(null);
  const [requestedCriteriaChoiceUser, setRequestedCriteriaChoiceUser] = useState<Collection<CriteriaChoiceUserType>>(null);
  const [sports, setSports] = useState<Collection<SportType>>(null);
  const [sportsCriterion, setSportsCriterion] = useState<CriteriaType[]>(null);
  const [timeslots, setTimeslots] = useState<Collection<TimeSlotType>>(null);
  const [selectedTimeslots, setSelectedTimeslots] = useState<Collection<SelectedTimeSlotType>>(null);

  const { startRequest, handleSuccess } = useRequestState();

  const initialize = useCallback(async () => {
    startRequest();

    const { user_status, criterion_status } = interaction.current_user_interaction_status;

    const { status, answers: fetchedAnswers, contributionId, contributions: fetchedContributions } = await getContribution(
      user_status === 'MATCH' && canInitialize({ interaction }),
      interaction.id,
      interaction['@id'],
      contributor['@id'],
    );

    const {
      mandatoryCriteria: fetchedMandatoryCriteria,
      mandatoryCriteriaChoiceUser: fetchedMandatoryCriteriaChoiceUser,
      requestedCriteria: fetchedRequestedCriteria,
      requestedCriteriaChoiceUser: fetchedRequestedCriteriaChoiceUser,
      sports: fetchedSports,
      sportsCriterion: fetchedSportsCriterion,
    } = await fetchPendingCriteria({
      criterionStatus: criterion_status,
      created_for: contributor.id,
    });

    setMandatoryCriteria(fetchedMandatoryCriteria);
    setMandatoryCriteriaChoiceUser(fetchedMandatoryCriteriaChoiceUser);
    setRequestedCriteria(fetchedRequestedCriteria);
    setRequestedCriteriaChoiceUser(fetchedRequestedCriteriaChoiceUser);
    setSports(fetchedSports);
    setSportsCriterion(fetchedSportsCriterion);

    if (interaction.time_slots?.length) {
      const { timeslots, selectedTimeslots } = await fetchTimeslots(
        interaction.id,
        !canInitialize({ interaction }) && contributionId,
      );

      setTimeslots(timeslots);
      setSelectedTimeslots(selectedTimeslots);
    }

    setUserStatus(status);
    setAnswers(fetchedAnswers);
    setContributions(fetchedContributions);

    handleSuccess();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    initialize();
  }, [initialize]);

  if (!userStatus) {
    return <MainLoader />;
  }

  return (
    <ContributorProvider
      contributor={contributor}
      contributions={contributions}
      answers={answers}
      status={userStatus}
    >
      <TimeslotsProvider
        timeslots={timeslots?.['hydra:member']}
        selectedTimeslots={selectedTimeslots?.['hydra:member']}
      >
        <ProfileCompletionProvider
          requestedCriteria={requestedCriteria?.['hydra:member']}
          requestedCriteriaChoiceUser={requestedCriteriaChoiceUser?.['hydra:member']}
          mandatoryCriteria={mandatoryCriteria?.['hydra:member']}
          mandatoryCriteriaChoiceUser={mandatoryCriteriaChoiceUser?.['hydra:member']}
          shouldUpdateSports={!!sportsCriterion?.length}
        >
          <CriteriaWrapper interaction={interaction} sports={sports}>
            {children}
          </CriteriaWrapper>
        </ProfileCompletionProvider>
      </TimeslotsProvider>
    </ContributorProvider>
  );
};
